import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap, take, timeout } from 'rxjs/operators';
import { LocaleService, OptimDecision, OptimService,ConfigurationService, Cacheable } from '@ciam/library';
import { MFAPreferencesResponse } from 'src/app/shared/models/MFAPreferencesResponse';

export enum PARAMS {
  MOCK = 'mock',
  DEFAULT_DELIVERY_METHOD = 'deliverymethod',
  status = 'status'
}

@Injectable({
  providedIn: 'root'
})
export class MFAPreferencesService {

  constructor(
    private http: HttpClient,
    private config: ConfigurationService,
    private optimService: OptimService,
    private localeService: LocaleService) { }

  getUserMFAPreferences(twoFactorEditDecoupleFlag: boolean, queryParams?: any): Observable<MFAPreferencesResponse> {
    let userMFAPreferencesUrl = twoFactorEditDecoupleFlag ? this.config.select<string>('endpoints.mfaPreferences.url')
    : this.config.select<string>('endpoints.mfaPreferencesV3.url');
    if (queryParams?.params && queryParams.get(PARAMS.MOCK) === 'true') {
      const queryParamStatus = queryParams.get(PARAMS.status);
      if (queryParamStatus === '200' || queryParamStatus === 'stateful') {
        const defaultDeliveryMethod = queryParams.get(PARAMS.DEFAULT_DELIVERY_METHOD);
        const deliveryMethod = (defaultDeliveryMethod === 'call' || defaultDeliveryMethod === 'text') ?
          defaultDeliveryMethod : 'email';
        userMFAPreferencesUrl += `?defaultDeliveryMethod=${deliveryMethod}`;
      } else { }
      userMFAPreferencesUrl += (queryParamStatus === '200' || queryParamStatus === 'stateful') ? ''
        : this.getStatus(queryParamStatus);
    } else { }
    return twoFactorEditDecoupleFlag ? this.http.get(userMFAPreferencesUrl, { withCredentials: true })
    .pipe(
      take(1),
      timeout(Number(this.config.select<number>('endpoints.mfaPreferences.timeout'))))
      : this.http.get(userMFAPreferencesUrl, { withCredentials: true });
  }

  // TODO clean up the query param intercept for all functions here
  /**
   * This retrieves user's MFA preferences.
   */
  getUserMFAPreferences2(): Observable<MFAPreferencesResponse> {
    const userMFAPreferencesUrl = this.config.select<string>('endpoints.mfaPreferences.url');
    return this.http.get(userMFAPreferencesUrl, { withCredentials: true }).pipe(take(1));
  }

  updateMFAOnLogin(flag: boolean, queryParams: any): Observable<MFAPreferencesResponse> {
    return this.optimService.decide$('wpro_2fa_on_edit', { country: `${this.localeService.solveLocale().country}` }).pipe(switchMap((result: OptimDecision) => {
      const body = { mfaOnLogin: flag };
      let urlMfaPreference: string;
      if (result.enabled) {
        urlMfaPreference = this.config.select<string>('endpoints.mfaPreferencesV3.url');
      }
      else {
        urlMfaPreference = this.config.select<string>('endpoints.mfaPreferences.url');
      }
      if (queryParams.params && queryParams.get(PARAMS.MOCK) === 'true') {
        const queryParamStatus = queryParams.get(PARAMS.status);
        urlMfaPreference += this.getStatus(queryParamStatus);
      }

      return this.http.put(urlMfaPreference, body, { withCredentials: true }).pipe(
        take(1),
        timeout(Number(this.config.select<number>('endpoints.mfaPreferences.timeout'))));
    }));
  }

  /**
   * This function restricts 2fa code requirement on login
   *
   * @param flag
   */
  restrictOnLogin(flag: boolean): Observable<MFAPreferencesResponse> {
    const userMFAPreferencesUrl = this.config.select<string>('endpoints.mfaPreferences.url');
    const body = { mfaOnLogin: flag };
    return this.http.put(userMFAPreferencesUrl, body, { withCredentials: true }).pipe(
      take(1),
        timeout(Number(this.config.select<number>('endpoints.mfaPreferences.timeout')))
    );
  }

  updateUserMFAPreferences(deliveryType: string): Observable<MFAPreferencesResponse> {
    const userMFAPreferencesUrl =   this.config.select<string>('endpoints.mfaPreferences.url') ;
    const body = { deliveryChannelPreference: deliveryType };
    return   this.http.put(userMFAPreferencesUrl, body, { withCredentials: true })
    .pipe(take(1),
          timeout(Number(this.config.select<number>('endpoints.mfaPreferences.timeout'))));
  }

  restrictOutsideCountryLogin(flag: boolean, queryParams: any): Observable<MFAPreferencesResponse> {
    let userMFAPreferencesUrl =   this.config.select<string>('endpoints.mfaPreferences.url') ;
    const body = { restrictOutsideCountryLogin: flag };
    // To differentiate two wire mock API response. It will execute if mock and mockemailasuserid queryParams available on URL
    if (queryParams.params && queryParams.get(PARAMS.MOCK) === 'true') {
      const queryParamStatus = queryParams.get(PARAMS.status);
      userMFAPreferencesUrl += this.getStatus(queryParamStatus);
    } else { }
    return  this.http.put(userMFAPreferencesUrl, body, { withCredentials: true })
    .pipe(take(1),
          timeout(Number(this.config.select<number>('endpoints.mfaPreferences.timeout'))));
  }

  /**
   * This changes 2fa restriction on location
   *
   * @param flag
   */
  restrictLocation(flag: boolean): Observable<MFAPreferencesResponse> {
    const userMFAPreferencesUrl = this.config.select<string>('endpoints.mfaPreferences.url');
    const body = { restrictOutsideCountryLogin: flag };
    return this.http.put(userMFAPreferencesUrl, body, { withCredentials: true });
  }

  getStatus(paramsStatus: any): any {
    // Returns the correct status for mocks
    return (paramsStatus === '400') ? '?status=400' :
      (paramsStatus === '401') ? '?status=401' :
        (paramsStatus === '403') ? '?status=403' :
          (paramsStatus === '404') ? '?status=404' :
            (paramsStatus === '500') ? '?status=500' :
              (paramsStatus === 'stateful') ? '?status=stateful' :
                '';
  }

  //B-999556 update secondry phone number prefecence for MFA
  updatePhoneNumForMFAPrefrenceType(preferredDeliveryTyp: string, deliveryChannelPref: string, queryParams: any): Observable<MFAPreferencesResponse> {
    let userMFAPreferencesV3Url = this.config.select<string>('endpoints.mfaPreferencesV3.url');
    const body = { preferredDeliveryType: preferredDeliveryTyp, deliveryChannelPreference: deliveryChannelPref };
    if (queryParams.params && queryParams.get(PARAMS.MOCK) === 'true') {
      const queryParamStatus = queryParams.get(PARAMS.status);
      if (queryParamStatus === '200' || queryParamStatus === 'stateful') {
        // const preferredDeliveryMethod = queryParams.get(PARAMS.PREFERRED_DELIVERY_TYPE);
        userMFAPreferencesV3Url += `?preferredDeliveryType=${preferredDeliveryTyp}`;
      } else { }
      userMFAPreferencesV3Url += (queryParamStatus === '200' || queryParamStatus === 'stateful') ? ''
        : this.getStatus(queryParamStatus);
    }
    else { }
    return this.http.put(userMFAPreferencesV3Url, body, { withCredentials: true });
  }
}
